import { Avatar, CardContent, FormHelperText, Grid, Pagination, Typography } from "@mui/material";
import { APP_PAGE_SIZE_DEFAULT } from "app/appConstants";
import { AppTransComponents } from "app/appTrans";
import { MprcCard, MprcCardActions, MprcConfirmDialog, MprcContainer, MprcIconButtonMoreOptionsDropdown, MprcJoinComponents, MprcLink, MprcRequestErrorReport, MprcRequestIndicator, MprcSpinnerIndicator, MprcText } from "components";
import { ButtonAdd } from "components/buttons";
import { MprcFilterCount, MprcFilterLine } from "components/filter";
import { MprcCardFilterBox } from "components/filter/MprcCardFilterBox";
import { IconButtonBlocked } from "components/iconButtons";
import { AutoCompleteDataModel } from "components/MprcAutoComplete";
import { MprcDropdownBuildGroupedItems, MprcDropdownItems } from "components/MprcIconButtonDropdown";
import { MprcListItemTitle } from "components/MprcListItemTitle";
import { MprcSearchTextField, MprcSearchTextFieldHandle } from "components/MprcSearchTextField";
import { AuditLogEntityType, TopicChallengeResponse, TopicChallengeResponsePaginatedList, TopicSelectorMode } from "generated/CoreApi_v1";
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { DashboardPaths } from "routes/DashboardRouter";
import { EditTopicChallengeDialog } from "views/assessment/topicChallenge/Home/EditTopicChallenge/EditTopicChallenge";
import { AuditLogHistoryFooter } from "views/common/AuditLog/AuditLogHistoryFooter";
import { TopicSelectorHierarchicalDrawer } from "views/foundation/topic/drawers/TopicSelectorHierarchicalDrawer";

////////////////////////////////////////////////////////////////////////////

type TopiChallengeListFilter = {
    searchText: string;
    suspended: boolean;
};

type TopicChallengeMenuOptions = "infoPanel" | "suspend" | "reactive" | "remove";

////////////////////////////////////////////////////////////////////////////

export const ListTopicChallenge: React.FC = () => {

    const api = useCoreApi();
    const { t } = useTranslation("assessment");
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const [filter, setFilter] = useState<TopiChallengeListFilter>({ searchText: "", suspended: false });
    const [page, setPage] = useState<number>(1);
    const [loading, setLoading] = useState<boolean>(true);
    const [posting, setPosting] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [viewData, setViewData] = useState<TopicChallengeResponsePaginatedList | null>(null);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [dialog, setDialog] = useState<any>();

    const searchTextField = useRef<MprcSearchTextFieldHandle | null>(null);

    ///
    const filtered = !!filter.searchText && filter.searchText.length > 0;

    const activeOrSuspendTopic = (challenge: TopicChallengeResponse, index: number) => {
        setPosting(true);
        api.v1.topicChallenge.changeChallengeStatus({ topicId: challenge.topicId, disableChallenge: !challenge.disableChallenge })
            .then((result) => {
                var newItems = [...viewData!.items];
                newItems[index].disableChallenge = result.data;
                setViewData({ ...viewData!, items: newItems });
            })
            .catch(e => setViewError(e))
            .finally(() => setPosting(false));
    };

    ///
    const handleSelectMenuOption = (option: TopicChallengeMenuOptions, challenge: TopicChallengeResponse, index: number) => {
        switch (option) {

            case "infoPanel":
                navigate(DashboardPaths.ChallengePanel, { state: { topic: { key: challenge.topicId, label: challenge.name, thumbnailUrl: challenge.thumbnailUrl } as AutoCompleteDataModel } });
                break;

            case "suspend":
            case "reactive":
                activeOrSuspendTopic(challenge, index);
                break;

            case "remove":
                setDialog(<MprcConfirmDialog
                    open
                    severity="danger"
                    message={<span>
                        <Trans
                            i18nKey="TopicChallenge.List.ConfirmRemoveMessage"
                            values={{ name: challenge.name }}
                            components={AppTransComponents}
                            t={t}
                        />
                    </span>}
                    onClose={
                        (result) => {
                            if (result) {
                                setDeleteId(challenge.topicId);
                            }
                            setDialog(null);
                        }
                    }
                />);
                break;
        }
    };

    ///
    const getMenuItems = useCallback((challenge: TopicChallengeResponse): MprcDropdownItems<TopicChallengeMenuOptions> => {
        const group0: MprcDropdownItems<TopicChallengeMenuOptions> = [
            { text: t("TopicChallenge.List.InfoPanelAction"), value: "infoPanel" },
        ];

        if (challenge.disableChallenge)
            group0.push({ text: t("TopicChallenge.List.ReactiveAction"), value: "reactive" });
        else
            group0.push({ text: t("TopicChallenge.List.SuspendAction"), value: "suspend" });

        const group1: MprcDropdownItems<TopicChallengeMenuOptions> = [
            { text: <MprcText color="error">{t("Actions.Delete", { ns: "shared" })}</MprcText>, value: "remove" }
        ];

        return MprcDropdownBuildGroupedItems<TopicChallengeMenuOptions>([group0, group1]);

    }, [viewData]);

    ///
    const resetFilters = () => {
        searchTextField.current?.reset();
        setFilter({ searchText: "", suspended: false });
        setLoading(true);
        setPage(1);
    };

    ///
    const handleFilter = (data: Partial<TopiChallengeListFilter>) => {
        setFilter(state => ({ ...state, ...data }));
        setLoading(true);
        setPage(1);
    };

    ///
    const handleEditTopic = (topicId: string) => {
        setDialog(
            <EditTopicChallengeDialog
                topicId={topicId}
                onClose={(success) => {
                    if (success) setLoading(true);
                    setDialog(null);
                }}
            />
        );
    };

    ///
    const buildSecondLine = (item: TopicChallengeResponse) => {
        let c: React.ReactNode[] = [];

        if (item.disableChallenge) {
            c.push(<Typography variant="bodyNotice">{t("TopicChallenge.List.DisableChallengeText")}</Typography>);
        }

        if (item.availableQuestions > 0) {
            c.push(<Typography variant="caption">{t("TopicChallenge.List.AvailableQuestionsText", { count: item.availableQuestions })}</Typography>);
        }

        if (item.totalAudience > 0) {
            c.push(<Typography variant="caption">{t("TopicChallenge.List.TotalAudienceText", { count: item.totalAudience })}</Typography>);
        }

        if (item.usersAnswered > 0) {
            c.push(
                <MprcLink onClick={() => handleSelectMenuOption("infoPanel", item, 0)}>
                    <Typography variant="caption" color="highlight.main">
                        {t("TopicChallenge.List.UsersAnswerText", { count: item.usersAnswered })}
                    </Typography>
                </MprcLink>
            );
        }

        return <Typography variant="caption"><MprcJoinComponents components={c} separator={<span>&nbsp;&bull;&nbsp;</span>} /></Typography>;
    };

    ///
    async function addTopicChallenge(topicId: string) {
        setPosting(true);

        try {
            var result = await api.v1.topicChallenge.addTopicChallenge({ topicId });
            if (result.data) {
                setLoading(true);
            } else {
                enqueueSnackbar(t("TopicChallenge.List.TopicAlreadyAddedMessage"), { variant: "error" });
                handleEditTopic(topicId);
            }
        }
        catch (e) {
            setViewError(e);
        }
        finally {
            setPosting(false);
        }
    }

    ///
    const handleNew = () => {
        setDialog(
            <TopicSelectorHierarchicalDrawer
                mode={TopicSelectorMode.Normal}
                onClose={(topic) => {
                    if (topic && topic.length > 0) addTopicChallenge(topic[0].topicId);
                    setDialog(null);
                }}
            />
        );
    };

    ////////////////////////////////////////////////////////////////////////////
    /// EFFECT'S
    ////////////////////////////////////////////////////////////////////////////

    ///
    useEffect(() => {
        async function getData() {
            try {
                var result = await api.v1.topicChallenge.topicsChallenge({
                    pageNumber: page,
                    pageSize: APP_PAGE_SIZE_DEFAULT,
                    searchText: filter.searchText ?? null,
                    suspended: filter.suspended
                });
                setViewData(result.data);
            } catch (e) {
                setViewData(null);
                setViewError(e);
            } finally {
                setLoading(false);
            }
        }

        if (loading) getData();
    }, [loading]);

    // Ao remover
    useEffect(() => {
        async function postData() {
            try {
                if (deleteId) {
                    var result = await api.v1.topicChallenge.removeTopicChallenge({ topicId: deleteId });
                    if (result) {
                        enqueueSnackbar(t("TopicChallenge.List.DeleteSuccessMessage"), { variant: "success" });
                        setLoading(true);
                    }
                    else {
                        enqueueSnackbar(t("TopicChallenge.List.DeleteErrorMessage"), { variant: "error" });
                    }
                }
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setDeleteId(null);
            }
        }

        postData();

    }, [deleteId]);

    ////////////////////////////////////////////////////////////////////////////
    /// RENDER
    ////////////////////////////////////////////////////////////////////////////

    ///
    const RenderFilter = (
        <MprcCardFilterBox>
            <MprcFilterLine>
                <div>
                    <ButtonAdd
                        size="large"
                        text={t("TopicChallenge.List.ButtonAddText")}
                        color="inherit"
                        onClick={() => handleNew()}
                    />
                </div>
                <div>
                    <IconButtonBlocked
                        color={filter.suspended ? "warning" : "inherit"}
                        title={t("TopicChallenge.List.SuspendTooltip")}
                        onClick={() => {
                            setFilter({ ...filter, suspended: !filter.suspended });
                            setLoading(true);
                        }}
                    />
                </div>
            </MprcFilterLine>
            <MprcFilterLine>
                <div>
                    <FormHelperText component="div" sx={{ mt: 0 }}>
                        {t("TopicChallenge.List.Hint_L1")}
                    </FormHelperText>
                </div>
            </MprcFilterLine>
            <MprcFilterLine>
                <div>
                    <FormHelperText component="div" sx={{ mt: 0 }}>
                        {t("TopicChallenge.List.Hint_L2")}
                    </FormHelperText>
                </div>
            </MprcFilterLine>
            <MprcSearchTextField
                autoFocus
                initialValue={filter.searchText}
                confirmMode="debounce"
                ref={searchTextField}
                onConfirm={(value) => handleFilter({ searchText: value })}
                placeholder={t("shared:Search.TextPlaceholder")}
            />
        </MprcCardFilterBox>
    );

    ///
    const RenderContent = () => {
        return (
            <CardContent>
                {loading
                    ? <MprcSpinnerIndicator />
                    : viewData && (
                        <MprcContainer>
                            <MprcContainer>
                                <MprcFilterCount
                                    loading={loading}
                                    isEmpty={viewData.totalRows == 0}
                                    countText={t("TopicChallenge.List.RecordsFoundText", { count: viewData.totalRows })}
                                    filter={{
                                        active: filtered,
                                        text: t("TopicChallenge.List.RecordsFilteredText", { count: viewData.totalRows }),
                                        onResetFilter: resetFilters
                                    }}
                                />
                            </MprcContainer>

                            <Grid container justifyContent="center" alignItems="center" spacing={1}>
                                {
                                    viewData.items.map((item, index) => {
                                        return (
                                            <Grid key={index} width="100%" size={{ xs: 12 }}>
                                                <Grid container justifyContent="flex-start" alignItems="flex-start" spacing={1} wrap="nowrap" width="100%">
                                                    <Grid>
                                                        <MprcLink onClick={() => handleEditTopic(item.topicId)}>
                                                            <Avatar
                                                                sx={{ width: 48, height: 48 }}
                                                                src={item.thumbnailUrl}
                                                            />
                                                        </MprcLink>
                                                    </Grid>
                                                    <Grid width="100%">
                                                        <Grid container justifyContent="flex-start" alignItems="center" spacing={1}>
                                                            <Grid size="grow" overflow="hidden">
                                                                <Grid container alignItems="center" flexWrap="nowrap" width="100%">
                                                                    <Grid overflow="hidden">
                                                                        <MprcListItemTitle
                                                                            onClick={() => handleEditTopic(item.topicId)}
                                                                        >
                                                                            {item.name}
                                                                        </MprcListItemTitle>
                                                                    </Grid>
                                                                    <Grid>
                                                                        <MprcIconButtonMoreOptionsDropdown
                                                                            color="muted"
                                                                            onChange={(selection) => handleSelectMenuOption(selection, item, index)}
                                                                            items={getMenuItems(item)}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                        <Grid>
                                                            <Grid
                                                                container
                                                                justifyContent="flex-start"
                                                                alignItems="center"
                                                                spacing={0}
                                                                sx={(theme) => ({ mt: theme.spacing(-.5) })}
                                                            >
                                                                {buildSecondLine(item)}
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        );
                                    })
                                }
                            </Grid>
                        </MprcContainer>
                    )
                }
            </CardContent>
        );
    };

    ///
    const RenderFooter = () => {
        return loading || viewData == null || viewData.totalPages == 1
            ? null
            : (
                <MprcCardActions>
                    {
                        viewData && viewData?.totalPages > 1 &&
                        <Pagination
                            shape="rounded"
                            color="primary"
                            page={viewData.pageNumber}
                            count={viewData.totalPages}
                            onChange={(e, pageNumber) => {
                                setPage(pageNumber);
                                setLoading(true);
                            }}
                        />
                    }
                </MprcCardActions>
            );
    };

    ///
    return (
        <>
            {dialog}

            <MprcRequestErrorReport error={viewError} />
            <MprcRequestIndicator visible={posting || deleteId != null} type="post" />

            {
                !viewError &&
                (
                    <>
                        <MprcContainer>
                            <MprcCard>
                                {RenderFilter}

                                {RenderContent()}

                                {RenderFooter()}
                            </MprcCard>
                        </MprcContainer>

                        {
                            !loading &&
                            <MprcContainer>
                                <AuditLogHistoryFooter entityType={AuditLogEntityType.Challenge} />
                            </MprcContainer>
                        }
                    </>
                )
            }
        </>
    );
};
